import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CircleNotch } from "@phosphor-icons/react";
import { LogoMark, Wordmark } from "@/components/Logo";

// Consumes the link from the backend's send_instant_access_email (sent after a Stripe
// purchase, either by the checkout.session.completed webhook or by /thank-you's
// POST /billing/confirm) — this is the only way a Stripe-provisioned account gets a
// password and logs in for the first time.
export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const { afterAuth } = useAuth();
  const nav = useNavigate();

  const submit = async () => {
    if (!token) { toast.error("This link is missing its token — copy the full link from your email."); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const r = await api.setPassword(token, password);
      await afterAuth(r.data.user);
      toast.success("Password set — welcome to JumpSEO");
      nav("/");
    } catch (e) {
      toast.error(apiError(e, "Couldn't set your password"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center justify-center gap-3 mb-8">
          <LogoMark size={36} />
          <Wordmark className="text-lg text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-1 text-center">Set your password</h2>
        <p className="text-sm mb-6 text-center" style={{ color: "var(--text-2)" }}>
          One last step — choose a password to log in to JumpSEO.
        </p>
        {!token ? (
          <p className="text-sm text-center" style={{ color: "var(--text-2)" }}>
            This link is missing its token. Copy the full link from your email, or{" "}
            <Link to="/login" className="font-semibold hover:underline" style={{ color: "var(--accent)" }}>sign in</Link>{" "}
            if you already have a password.
          </p>
        ) : (
          <div className="space-y-3">
            <input data-testid="setpw-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
            <input data-testid="setpw-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Confirm password" className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
            <button data-testid="setpw-submit" onClick={submit} disabled={loading} className="w-full px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
              {loading ? <CircleNotch size={16} className="animate-spin" /> : null}
              Set password &amp; sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
